import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  getNotes,
  createNote,
  updateNote,
  deleteNote,
} from '../../api/note.api.js';
import NoteList from './components/NoteList';
import NoteDetail from './components/NoteDetail';
import NoteForm from './components/NoteForm';
import NoteImport from './components/NoteImport';

function NotesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const data = await getNotes();
      console.log('Notes loaded from API:', data);
      setNotes(data);
    } catch (error) {
      console.error('Error loading notes:', error);
      showSnackbar('Failed to load notes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddNote = () => {
    setEditingNote(null);
    setAddDialogOpen(true);
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setEditDialogOpen(true);
  };

  const handleViewNote = (note) => {
    setSelectedNote(note);
    if (isMobile) {
      setMobileDrawerOpen(false); // Close drawer on mobile when note is selected
    }
  };

  const handleDeleteNote = async (note) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await deleteNote(note._id);
        await loadNotes();
        if (selectedNote && selectedNote._id === note._id) {
          setSelectedNote(null);
        }
        showSnackbar('Note deleted successfully');
      } catch (error) {
        console.error('Error deleting note:', error);
        showSnackbar('Failed to delete note', 'error');
      }
    }
  };

  const handleSaveNote = async (noteData) => {
    try {
      if (editingNote) {
        await updateNote(editingNote._id, noteData);
        showSnackbar('Note updated successfully');
      } else {
        await createNote(noteData);
        showSnackbar('Note created successfully');
      }

      await loadNotes();
      setAddDialogOpen(false);
      setEditDialogOpen(false);
      setEditingNote(null);
    } catch (error) {
      console.error('Error saving note:', error);
      showSnackbar('Failed to save note', 'error');
    }
  };

  const handleImportNotes = () => {
    setImportDialogOpen(true);
  };

  const handleImportComplete = (result) => {
    setImportDialogOpen(false);
    if (result.success) {
      loadNotes();
      showSnackbar('Notes imported successfully');
    } else {
      showSnackbar('Failed to import notes', 'error');
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setEditingNote(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingNote(null);
  };

  const notesWithId = (notes || []).map((note) => ({ ...note, id: note._id || note.id }));
  console.log('Notes with id:', notesWithId);

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile App Bar */}
        <AppBar position="static" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Notes
            </Typography>
            <Button
              color="inherit"
              startIcon={<AddIcon />}
              onClick={handleAddNote}
            >
              Add
            </Button>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {selectedNote ? (
            // Note Detail View
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  <NoteDetail
                    note={selectedNote}
                    onEdit={handleEditNote}
                    onDelete={handleDeleteNote}
                  />
                </Box>
              </Paper>
            </Box>
          ) : (
            // Empty State
            <Box sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              p: 3,
            }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a Note
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Choose a note from the list to view details
              </Typography>
            </Box>
          )}
        </Box>

        {/* Mobile Drawer for Note List */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: isSmallMobile ? '100%' : 320,
              boxSizing: 'border-box',
            },
          }}
        >
          <Box sx={{
            p: 2, height: '100%', display: 'flex', flexDirection: 'column',
          }}
          >
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <NoteList
                notes={notesWithId}
                loading={loading}
                onAdd={handleAddNote}
                onEdit={handleEditNote}
                onDelete={handleDeleteNote}
                onView={handleViewNote}
                onImport={handleImportNotes}
                selectedNote={selectedNote}
              />
            </Box>
          </Box>
        </Drawer>

        {/* Add Note Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              maxHeight: isSmallMobile ? '100%' : '90vh',
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Add New Note
            </Typography>
            <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <NoteForm
              note={null}
              onSave={handleSaveNote}
              onCancel={handleCloseAddDialog}
              loading={loading}
            />
          </Box>
        </Dialog>

        {/* Edit Note Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              maxHeight: isSmallMobile ? '100%' : '90vh',
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Edit Note
            </Typography>
            <IconButton onClick={handleCloseEditDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <NoteForm
              note={editingNote}
              onSave={handleSaveNote}
              onCancel={handleCloseEditDialog}
              loading={loading}
            />
          </Box>
        </Dialog>

        {/* Import Dialog */}
        <Dialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Import Notes
            </Typography>
            <IconButton onClick={() => setImportDialogOpen(false)} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <NoteImport
              onClose={() => setImportDialogOpen(false)}
              onImportComplete={handleImportComplete}
            />
          </Box>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column',
    }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Notes
      </Typography>

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        {/* Note List */}
        <Grid item xs={12} md={selectedNote ? 8 : 12}>
          <Paper sx={{
            height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
          }}
          >
            <NoteList
              notes={notesWithId}
              loading={loading}
              onAdd={handleAddNote}
              onEdit={handleEditNote}
              onDelete={handleDeleteNote}
              onView={handleViewNote}
              onImport={handleImportNotes}
              selectedNote={selectedNote}
            />
          </Paper>
        </Grid>

        {/* Note Detail */}
        {selectedNote && (
          <Grid item xs={12} md={4}>
            <Paper sx={{
              height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
            }}
            >
              <NoteDetail
                note={selectedNote}
                onEdit={handleEditNote}
                onDelete={handleDeleteNote}
              />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add Note Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Add New Note
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <NoteForm
            note={null}
            onSave={handleSaveNote}
            onCancel={handleCloseAddDialog}
            loading={loading}
          />
        </Box>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Edit Note
          </Typography>
          <IconButton onClick={handleCloseEditDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <NoteForm
            note={editingNote}
            onSave={handleSaveNote}
            onCancel={handleCloseEditDialog}
            loading={loading}
          />
        </Box>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Import Notes
          </Typography>
          <IconButton onClick={() => setImportDialogOpen(false)} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <NoteImport
            onClose={() => setImportDialogOpen(false)}
            onImportComplete={handleImportComplete}
          />
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default NotesPage;
